import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { PhotoCamera, QrCodeScanner, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import CameraScanDialog from "./CameraScanDialog";

const FIELD_MIN_LENGTH = 3;
const HARDWARE_MIN_LENGTH = 6;
const SCANNER_GAP_MS = 50;
const ENTER_SUFFIX_MS = 220;
const AUTO_SUBMIT_MS = 90;

const BarcodeScanner = ({
  onScan,
  listenHardware = true,
  autoFocus = false,
  placeholder,
  size = "medium",
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [listening, setListening] = useState(listenHardware);
  const inputRef = useRef(null);
  const valueRef = useRef("");
  const lastScanRef = useRef({ code: "", at: 0 });
  const bufferRef = useRef("");
  const lastKeyAtRef = useRef(0);
  const burstRef = useRef(false);
  const autoSubmitRef = useRef(null);
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const setScanValue = (next) => {
    valueRef.current = next;
    setValue(next);
  };

  const focusScanField = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const emitScan = useCallback(
    (rawCode, { refocus = true } = {}) => {
      const code = String(rawCode || "").trim();
      if (!code || disabled) return;
      const now = Date.now();
      if (
        code === lastScanRef.current.code &&
        now - lastScanRef.current.at < 700
      ) {
        return;
      }
      lastScanRef.current = { code, at: now };
      setScanValue("");
      if (typeof onScanRef.current === "function") {
        onScanRef.current(code);
      }
      if (refocus || autoFocus) {
        focusScanField();
      }
    },
    [disabled, autoFocus],
  );

  const handleManualSubmit = (rawCode) => {
    emitScan(rawCode ?? valueRef.current ?? value, { refocus: true });
  };

  useEffect(() => {
    if (!listenHardware || disabled) return undefined;

    const clearAutoSubmit = () => {
      if (autoSubmitRef.current) {
        clearTimeout(autoSubmitRef.current);
        autoSubmitRef.current = null;
      }
    };

    const submitBuffer = () => {
      const buffered = String(bufferRef.current || "").trim();
      bufferRef.current = "";
      burstRef.current = false;
      clearAutoSubmit();
      if (buffered.length >= HARDWARE_MIN_LENGTH) {
        emitScan(buffered, { refocus: true });
      }
    };

    const onKeyDown = (event) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.isComposing) return;

      const target = event.target;
      const isBarcodeField = target?.dataset?.barcodeInput === "true";
      const key = typeof event.key === "string" ? event.key : "";
      if (!key) return;

      const now = Date.now();
      const delta = lastKeyAtRef.current ? now - lastKeyAtRef.current : 0;
      lastKeyAtRef.current = now;

      if (isBarcodeField) {
        bufferRef.current = "";
        burstRef.current = false;
        clearAutoSubmit();
        if (key === "Enter" || key === "Tab") {
          const typed = String(target?.value || valueRef.current || "").trim();
          if (typed.length >= FIELD_MIN_LENGTH) {
            event.preventDefault();
            event.stopPropagation();
            emitScan(typed, { refocus: true });
          }
        }
        return;
      }

      if (key === "Enter" || key === "Tab") {
        const buffered = String(bufferRef.current || "").trim();
        const fromGun =
          burstRef.current &&
          buffered.length >= HARDWARE_MIN_LENGTH &&
          delta <= ENTER_SUFFIX_MS;
        bufferRef.current = "";
        burstRef.current = false;
        clearAutoSubmit();
        if (fromGun) {
          event.preventDefault();
          event.stopPropagation();
          emitScan(buffered, { refocus: true });
        }
        return;
      }

      if (key.length !== 1) return;

      if (delta > SCANNER_GAP_MS) {
        bufferRef.current = key;
        burstRef.current = false;
        clearAutoSubmit();
        return;
      }

      bufferRef.current += key;
      burstRef.current = bufferRef.current.length >= 2;

      if (burstRef.current && bufferRef.current.length >= HARDWARE_MIN_LENGTH) {
        event.preventDefault();
        event.stopPropagation();
        clearAutoSubmit();
        autoSubmitRef.current = setTimeout(submitBuffer, AUTO_SUBMIT_MS);
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    setListening(true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      setListening(false);
      bufferRef.current = "";
      burstRef.current = false;
      clearAutoSubmit();
    };
  }, [listenHardware, disabled, emitScan]);

  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        fullWidth
        size={size}
        inputRef={inputRef}
        autoFocus={autoFocus}
        disabled={disabled}
        value={value}
        onChange={(event) => setScanValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === "Tab") {
            const typed = String(event.target?.value || valueRef.current || "");
            if (typed.trim()) {
              event.preventDefault();
              handleManualSubmit(typed);
            }
          }
        }}
        placeholder={placeholder || t("pharmacy.scanPlaceholder")}
        label={t("pharmacy.scanBarcode")}
        inputProps={{
          "data-barcode-input": "true",
          autoComplete: "off",
          autoCorrect: "off",
          autoCapitalize: "off",
          spellCheck: "false",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <QrCodeScanner color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {listenHardware && (
                <Chip
                  size="small"
                  color={listening ? "success" : "default"}
                  label={
                    listening
                      ? t("pharmacy.hardwareReady")
                      : t("pharmacy.hardwareIdle")
                  }
                  sx={{ mr: 1, display: { xs: "none", sm: "inline-flex" } }}
                />
              )}
              <Tooltip title={t("pharmacy.cameraScan")}>
                <span>
                  <IconButton
                    color="primary"
                    disabled={disabled}
                    onClick={() => setCameraOpen(true)}
                  >
                    <PhotoCamera />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("pharmacy.scanNow")}>
                <span>
                  <IconButton
                    color="primary"
                    disabled={disabled || !String(value || "").trim()}
                    onClick={() => handleManualSubmit()}
                  >
                    <Search />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
      />
      <CameraScanDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(code) => {
          setCameraOpen(false);
          emitScan(code, { refocus: true });
        }}
      />
    </Box>
  );
};

export default BarcodeScanner;
